import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, AlertCircle, DollarSign, TrendingUp, Users, Shield } from 'lucide-react';
import Header from '../../../components/ui/Header';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AccountLimits = () => {
  const navigate = useNavigate();
  const [user] = useState({
    id: 'admin-001',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@moneytransfer.com',
    role: 'admin'
  });

  const [limits, setLimits] = useState({
    // Transaction Limits
    dailyTransactionLimit: '50000',
    monthlyTransactionLimit: '200000',
    singleTransactionLimit: '25000',
    yearlyTransactionLimit: '1000000',
    
    // Account Tier Limits
    basicTierDaily: '5000',
    basicTierMonthly: '20000',
    verifiedTierDaily: '25000',
    verifiedTierMonthly: '100000',
    premiumTierDaily: '100000',
    premiumTierMonthly: '500000',
    
    // Recipient Limits
    maxRecipients: '50',
    maxNewRecipientsPerMonth: '10',
    
    // Velocity Limits
    maxTransactionsPerDay: '10',
    maxTransactionsPerHour: '3',
    
    // Risk-Based Limits
    highRiskCountryLimit: '10000',
    firstTimeRecipientLimit: '5000',
    
    // Currency Exchange Limits
    maxCurrencyPairs: '20',
    dailyExchangeLimit: '100000'
  });

  const [hasChanges, setHasChanges] = useState(false);

  const handleLimitChange = (field, value) => {
    setLimits(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    console.log('Saving account limits:', limits);
    // API call to save limits
    setHasChanges(false);
    alert('Account limits updated successfully');
  };

  const handleReset = () => {
    // Reset to default values
    setLimits({
      dailyTransactionLimit: '50000',
      monthlyTransactionLimit: '200000',
      singleTransactionLimit: '25000',
      yearlyTransactionLimit: '1000000',
      basicTierDaily: '5000',
      basicTierMonthly: '20000',
      verifiedTierDaily: '25000',
      verifiedTierMonthly: '100000',
      premiumTierDaily: '100000',
      premiumTierMonthly: '500000',
      maxRecipients: '50',
      maxNewRecipientsPerMonth: '10',
      maxTransactionsPerDay: '10',
      maxTransactionsPerHour: '3',
      highRiskCountryLimit: '10000',
      firstTimeRecipientLimit: '5000',
      maxCurrencyPairs: '20',
      dailyExchangeLimit: '100000'
    });
    setHasChanges(false);
  };

  const handleLogout = () => {
    navigate('/login');
  };

  const limitSections = [
    {
      title: 'Transaction Limits',
      icon: DollarSign,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      fields: [
        { key: 'singleTransactionLimit', label: 'Single Transaction Limit', prefix: '$' },
        { key: 'dailyTransactionLimit', label: 'Daily Transaction Limit', prefix: '$' },
        { key: 'monthlyTransactionLimit', label: 'Monthly Transaction Limit', prefix: '$' },
        { key: 'yearlyTransactionLimit', label: 'Yearly Transaction Limit', prefix: '$' }
      ]
    },
    {
      title: 'Account Tier Limits',
      icon: TrendingUp,
      color: 'text-success',
      bgColor: 'bg-success/10',
      fields: [
        { key: 'basicTierDaily', label: 'Basic Tier - Daily', prefix: '$' },
        { key: 'basicTierMonthly', label: 'Basic Tier - Monthly', prefix: '$' },
        { key: 'verifiedTierDaily', label: 'Verified Tier - Daily', prefix: '$' },
        { key: 'verifiedTierMonthly', label: 'Verified Tier - Monthly', prefix: '$' },
        { key: 'premiumTierDaily', label: 'Premium Tier - Daily', prefix: '$' },
        { key: 'premiumTierMonthly', label: 'Premium Tier - Monthly', prefix: '$' }
      ]
    },
    {
      title: 'Recipient & Velocity Limits',
      icon: Users,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      fields: [
        { key: 'maxRecipients', label: 'Maximum Recipients', prefix: '' },
        { key: 'maxNewRecipientsPerMonth', label: 'New Recipients Per Month', prefix: '' },
        { key: 'maxTransactionsPerDay', label: 'Transactions Per Day', prefix: '' },
        { key: 'maxTransactionsPerHour', label: 'Transactions Per Hour', prefix: '' }
      ]
    },
    {
      title: 'Risk-Based Limits',
      icon: Shield,
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      fields: [
        { key: 'highRiskCountryLimit', label: 'High Risk Country Limit', prefix: '$' },
        { key: 'firstTimeRecipientLimit', label: 'First-Time Recipient Limit', prefix: '$' },
        { key: 'maxCurrencyPairs', label: 'Max Currency Pairs', prefix: '' },
        { key: 'dailyExchangeLimit', label: 'Daily Exchange Limit', prefix: '$' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogout={handleLogout} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin-dashboard')}
            className="flex items-center text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Admin Dashboard
          </button>
          
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Account Limits Management</h1>
              <p className="text-muted-foreground mt-2">
                Configure transaction limits, account tiers, and risk-based restrictions
              </p>
            </div>
            
            <div className="flex gap-3">
              {hasChanges && (
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="border-muted-foreground/20"
                >
                  Reset Changes
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={!hasChanges}
                className="bg-primary hover:bg-primary/90"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>

        {/* Warning Notice */}
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6 flex items-start">
          <AlertCircle className="w-5 h-5 text-warning mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-foreground font-medium">Important</p>
            <p className="text-sm text-muted-foreground mt-1">
              Changes to account limits will affect all users immediately. Please review carefully before saving.
              Lower limits may impact active transactions.
            </p>
          </div>
        </div>

        {/* Limit Sections */}
        <div className="space-y-6">
          {limitSections?.map((section, idx) => (
            <div key={idx} className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center mb-6">
                <div className={`${section?.bgColor} p-3 rounded-lg mr-4`}>
                  <section.icon className={`w-6 h-6 ${section?.color}`} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">{section?.title}</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Configure {section?.title?.toLowerCase()} for the platform
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {section?.fields?.map((field) => (
                  <div key={field?.key} className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {field?.label}
                    </label>
                    <div className="relative">
                      {field?.prefix && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          {field?.prefix}
                        </span>
                      )}
                      <Input
                        type="number"
                        value={limits?.[field?.key]}
                        onChange={(e) => handleLimitChange(field?.key, e?.target?.value)}
                        className={`${field?.prefix ? 'pl-8' : 'pl-3'} bg-background`}
                        placeholder="Enter limit"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Current Status Summary */}
        <div className="mt-8 bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Current Limits Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-background rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Daily Limit</p>
              <p className="text-2xl font-bold text-foreground mt-1">
                ${parseInt(limits?.dailyTransactionLimit || 0)?.toLocaleString()}
              </p>
            </div>
            <div className="bg-background rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Monthly Limit</p>
              <p className="text-2xl font-bold text-foreground mt-1">
                ${parseInt(limits?.monthlyTransactionLimit || 0)?.toLocaleString()}
              </p>
            </div>
            <div className="bg-background rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Single Transaction</p>
              <p className="text-2xl font-bold text-foreground mt-1">
                ${parseInt(limits?.singleTransactionLimit || 0)?.toLocaleString()}
              </p>
            </div>
            <div className="bg-background rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Max Recipients</p>
              <p className="text-2xl font-bold text-foreground mt-1">
                {limits?.maxRecipients}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccountLimits;